// SPV Pool v1.0 PR 6 — manual reserve-fund adjustment dialog. Posts an
// amount + reason to the reserve-fund/manual-adjustment endpoint. Reason
// is required; the confirm button is blocked while it is blank.

import { useState } from "react";
import { Button, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { usePostReserveManualAdjustment } from "@/hooks/capitalMarkets/useSpvPools";

interface ManualAdjustmentDialogProps {
  poolId: string;
  open: boolean;
  onClose: () => void;
}

export function ManualAdjustmentDialog({
  poolId,
  open,
  onClose,
}: ManualAdjustmentDialogProps) {
  const toast = useToast();
  const adjust = usePostReserveManualAdjustment(poolId);

  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  const parsedAmount = Number(amount);
  const reasonValid = reason.trim().length > 0;
  const amountValid = amount.trim().length > 0 && !Number.isNaN(parsedAmount);
  const canSubmit = reasonValid && amountValid;

  const reset = () => {
    setAmount("");
    setReason("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    adjust
      .mutateAsync({ amount: parsedAmount, reason: reason.trim() })
      .then(() => {
        toast.success("Reserve fund adjustment recorded.");
        reset();
        onClose();
      })
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      dismissOnBackdropClick={false}
      title="Manual reserve fund adjustment"
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-space-2">
          <Button variant="text" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={adjust.isPending}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            Record adjustment
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-space-3">
        <label className="flex flex-col gap-space-1">
          <span className="text-label font-display uppercase tracking-label text-gold-shadow">
            Amount
          </span>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0"
            aria-label="Adjustment amount"
          />
          <span className="text-caption text-gray-mid">
            Use a negative number to record a draw.
          </span>
        </label>
        <label className="flex flex-col gap-space-1">
          <span className="text-label font-display uppercase tracking-label text-gold-shadow">
            Reason
          </span>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this adjustment being made?"
            aria-label="Adjustment reason"
            invalid={reason.length > 0 && !reasonValid}
          />
          <span className="text-caption text-gray-mid">
            A reason is required for every manual adjustment.
          </span>
        </label>
      </div>
    </Modal>
  );
}
